import React from "react";
import { MapPin, Phone, Mail, Globe } from "lucide-react";
import { GoogleMap } from "@/components/nursing-homes/GoogleMap";
import { useMediaQuery } from "@/hooks/use-media-query";

interface ContactTabProps {
  nursingHome: {
    standardized_address?: string;
    standardized_phone_number?: string;
    email?: string;
    official_website?: string;
  };
}

export function ContactTab({
  nursingHome
}: ContactTabProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-grandpal-beige p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-semibold mb-4">Contact Information</h2>
        <div className="space-y-4">
          {nursingHome.standardized_address && (
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="bg-grandpal-beige p-1.5 rounded-full">
                <MapPin className="h-4 w-4 text-grandpal-green" />
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base">Address</p>
                <p className="text-xs sm:text-sm text-grandpal-gray">{nursingHome.standardized_address}</p>
              </div>
            </div>
          )}
          
          {nursingHome.standardized_phone_number && (
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="bg-grandpal-beige rounded-full p-1.5 mt-0.5">
                <Phone className="h-4 w-4 text-grandpal-green" />
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base">Phone</p>
                <a 
                  href={`tel:${nursingHome.standardized_phone_number}`} 
                  className="text-xs sm:text-sm text-grandpal-gray hover:underline block py-1"
                >
                  {nursingHome.standardized_phone_number}
                </a>
              </div>
            </div>
          )}
          
          {nursingHome.email && (
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="bg-grandpal-beige rounded-full p-1.5 mt-0.5">
                <Mail className="h-4 w-4 text-grandpal-green" />
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base">Email</p>
                <a 
                  href={`mailto:${nursingHome.email}`} 
                  className="text-xs sm:text-sm text-grandpal-gray hover:underline block py-1 break-all"
                >
                  {nursingHome.email}
                </a>
              </div>
            </div>
          )}
          
          {nursingHome.official_website && (
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="bg-grandpal-beige rounded-full p-1.5 mt-0.5">
                <Globe className="h-4 w-4 text-grandpal-green" />
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base">Website</p>
                <a 
                  href={nursingHome.official_website} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-xs sm:text-sm text-grandpal-gray hover:underline block py-1"
                >
                  Visit Website
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Map - responsive height and collapsible on mobile */}
      <div className="bg-white rounded-lg shadow-sm border border-grandpal-beige overflow-hidden p-0">
        {nursingHome.standardized_address && (
          <div style={{ width: '100%', height: isMobile ? 350 : 560 }}>
            <GoogleMap 
              address={nursingHome.standardized_address}
              size={isMobile ? "full" : "full"}
              showCard={false}
              className="w-full h-full"
            />
          </div>
        )}
      </div>
    </div>
  );
}
